import { useCallback } from "react";
import { useApolloClient } from "@apollo/client";
import { Query, ScheduleInstanceFilterInput } from "@models/ISchema";
import { sessionAssignationStore } from "@context/sessionAssignation";
import type { SessionAssignationContext } from "@services/types/session-assignation/sessionAssignationTypes";
import assignmentScheduleInstancesQuery from "../graphql/assignmentScheduleInstances.query.graphql";
import sessionAssignationDataQuery from "../graphql/sessionAssignationData.query.graphql";

export const useSessionAssignationLoad = (
  context: SessionAssignationContext,
  store = sessionAssignationStore,
) => {
  const client = useApolloClient();
  const { scenarioId, originId, linkId } = context;
  return useCallback(async () => {
    const generation = store.getState().beginLoad({ scenarioId, originId, linkId });
    if (generation === null) return false;
    try {
      const filter: ScheduleInstanceFilterInput = { fields: { linkId: { eq: linkId } } };
      const [catalogs, schedules] = await Promise.all([
        client.query<Query>({
          query: sessionAssignationDataQuery,
          variables: { scenarioId, originId, linkId },
          fetchPolicy: "no-cache",
        }),
        client.query<Query>({
          query: assignmentScheduleInstancesQuery,
          variables: { scenarioId, originId, filter },
          fetchPolicy: "no-cache",
        }),
      ]);
      const sections = catalogs.data?.cube?.editorView?.sections;
      const weeks = catalogs.data?.cube?.editorWeeks;
      const blocks = catalogs.data?.data?.allBlocks;
      const instances = schedules.data?.cube?.scheduleInstances;
      if (
        !Array.isArray(sections) ||
        !Array.isArray(weeks) ||
        !Array.isArray(blocks) ||
        !Array.isArray(instances)
      )
        throw new Error("Incomplete assignment response");
      return store.getState().initialize(
        {
          context: { scenarioId, originId, linkId },
          sections: sections.filter(Boolean),
          weeks: weeks.filter(Boolean),
          blocks: blocks.filter(Boolean),
          instances: instances.filter(Boolean),
        },
        generation,
      );
    } catch {
      store.getState().failLoad(generation);
      return false;
    }
  }, [client, store, scenarioId, originId, linkId]);
};
